/**
 * SOLID Review
 * S — Strong. One concern: the server's and the database's health dots — asked every 15 s, never during active play.
 * O — Good. A new dot is one more entry of `STATUS_CHECKS`.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The dots, the checks the view draws.
 * D — Strong. One use case, the client view, the request contexts, the timers.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IServiceViewTimers } from './platform/IConfigDepViewPlatform';
import type { ClientRequests } from '../client/ClientRequests';
import { GamePhase } from '../../application/ports/ClientVocabulary';
import type { IPortClientStateView } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

const CHECK_HEALTH: TClientUseCaseInput<'checkServerHealth'> = {};
const POLL_INTERVAL_MS = 15_000;

export type TStatusValue = 'OK' | 'ERROR' | 'UNKNOWN';

/** One dot: its key, its label and the reading of the health answer it shows. */
export interface IConfigStatusCheck {
  readonly key: string;
  readonly dotLabel: string;
  readonly ariaLabel: string;
  readonly fromResponse: (data: Record<string, unknown>) => TStatusValue;
}

export const STATUS_CHECKS: readonly IConfigStatusCheck[] = Object.freeze([
  { key: 'server', dotLabel: 'SRV', ariaLabel: 'Server', fromResponse: (data: Record<string, unknown>) => (data['server'] === 'OK' ? 'OK' : 'ERROR') },
  { key: 'db', dotLabel: 'DB', ariaLabel: 'DB', fromResponse: (data: Record<string, unknown>) => (data['db'] === 'OK' ? 'OK' : 'ERROR') },
] as const);

/** The phases of active play: no poll then. */
const ACTIVE_PHASES: ReadonlySet<string> = Object.freeze(new Set<string>([
  GamePhase.ROLLING, GamePhase.PEEKING, GamePhase.CLAIMING, GamePhase.DECIDING, GamePhase.REVEALING,
]));

interface IStateHealthOwn {
  readonly health: Readonly<Record<string, TStatusValue>>;
}

export interface IStateHealth extends IStateHealthOwn {
  readonly isPaused: boolean;
}

/**
 * THE HEALTH DOTS (frontend-framework-oo P5, 2026-10-30, §28.297) — `components/layout/HealthIndicator.tsx`'s state and effect: the
 * Application's `checkServerHealth` asked at once and every 15 s while no round is in active play; an answer without a body marks the
 * server down and the rest unknown; an answer after the poll stopped is dropped.
 */
export class HealthIndicatorViewModel implements IServiceViewModel<IStateHealth> {
  private readonly state: ObservableState<IStateHealthOwn>;
  private readonly reactions = new Reactions<IStateHealth>([{
    deps: (s) => [s.isPaused],
    run: (s) => {
      if (s.isPaused) return undefined; // don't poll during active gameplay
      let cancelled = false;
      const check = async (): Promise<void> => {
        const answer = await this.useCases.checkServerHealth(this.requests.context(), CHECK_HEALTH);
        /* c8 ignore next -- guard against unmount during the async probe; not reproducible with React act() */
        if (cancelled) return;
        const body = answer.success ? answer.value.body : null;
        const health: Readonly<Record<string, TStatusValue>> = body !== null
          ? Object.fromEntries(STATUS_CHECKS.map((c) => [c.key, c.fromResponse(body)]))
          : { ...HealthIndicatorViewModel.allUnknown(), [STATUS_CHECKS[0]!.key]: 'ERROR' };
        const read: Partial<IStateHealthOwn> = { health };
        this.state.update(read);
      };
      void check();
      const id = this.timers.setInterval(check, POLL_INTERVAL_MS);
      return () => {
        cancelled = true;
        this.timers.clearInterval(id);
      };
    },
  }]);
  private shown: { readonly own: IStateHealthOwn; readonly isPaused: boolean; readonly dots: IStateHealth } | null = null;

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'checkServerHealth'>,
    private readonly view: IPortClientStateView,
    private readonly requests: ClientRequests,
    private readonly timers: IServiceViewTimers,
  ) {
    const unknown: IStateHealthOwn = { health: HealthIndicatorViewModel.allUnknown() };
    this.state = new ObservableState<IStateHealthOwn>(unknown);
  }

  private static allUnknown(): Readonly<Record<string, TStatusValue>> {
    return Object.fromEntries(STATUS_CHECKS.map((c) => [c.key, 'UNKNOWN' as TStatusValue]));
  }

  getSnapshot(): IStateHealth {
    const own = this.state.get();
    const isPaused = ACTIVE_PHASES.has(this.view.getSnapshot().phase);
    if (this.shown !== null && this.shown.own === own && this.shown.isPaused === isPaused) return this.shown.dots;
    const dots: IStateHealth = { health: own.health, isPaused };
    this.shown = { own, isPaused, dots };
    return dots;
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveView = this.view.subscribe(listener);
    return () => {
      leaveOwn();
      leaveView();
    };
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(_inputs: readonly unknown[], rendered: IStateHealth): void {
    this.reactions.stopChanged(rendered);
  }

  commit(_inputs: readonly unknown[], rendered: IStateHealth): void {
    this.reactions.startChanged(rendered);
  }
}
